'use client';

import { useState } from 'react';
import { checkTripAvailability, TripAvailability, AvailabilityStatus, Trip } from '@/lib/trips/api';
import { Input } from '@/components/FormElements/input';
import { Button } from '@/components/FormElements/button';

interface TripAvailabilityCheckerProps {
  trip: Trip;
}

function StatusBadge({ status }: { status: AvailabilityStatus }) {
  const config = {
    [AvailabilityStatus.AVAILABLE]: {
      label: 'Available',
      className: 'bg-green-100 text-green-800 border-green-200',
      dotClass: 'bg-green-500',
    },
    [AvailabilityStatus.LIMITED_SPOTS]: {
      label: 'Limited Spots',
      className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      dotClass: 'bg-yellow-500',
    },
    [AvailabilityStatus.FULLY_BOOKED]: {
      label: 'Fully Booked',
      className: 'bg-red-100 text-red-800 border-red-200',
      dotClass: 'bg-red-500',
    },
  };

  const { label, className, dotClass } = config[status];

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border ${className}`}
    >
      <span className={`w-2 h-2 rounded-full ${dotClass}`} />
      {label}
    </span>
  );
}

function ProgressBar({
  booked,
  max,
}: {
  booked: number;
  max: number;
}) {
  const percentage = max > 0 ? Math.min((booked / max) * 100, 100) : 0;
  const remaining = Math.max(max - booked, 0);

  let barColor = 'bg-green-500';
  if (remaining === 0) {
    barColor = 'bg-red-500';
  } else if (remaining <= 3) {
    barColor = 'bg-yellow-500';
  }

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-gray-600">
          {booked} booked / {max} total
        </span>
        <span className="font-medium text-gray-900">
          {remaining} spots left
        </span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-300 ${barColor}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

export default function TripAvailabilityChecker({ trip }: TripAvailabilityCheckerProps) {
  const [date, setDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [availability, setAvailability] = useState<TripAvailability | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isPrivate = trip.trip_type === 'Private';

  const handleCheck = async () => {
    if (!date) {
      setError('Please select a date');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await checkTripAvailability(trip.id!, date);
      if (response.data) {
        setAvailability(response.data);
      } else {
        setError('Failed to check availability');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to check availability');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Trip Availability
        </h3>
        {availability && (
          <StatusBadge status={availability.status} />
        )}
      </div>

      {isPrivate ? (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-blue-800">
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="font-medium">Private Trip</span>
          </div>
          <p className="text-blue-700 mt-1 text-sm">
            This is a private trip. All dates are available for booking.
          </p>
          <div className="mt-3">
            <StatusBadge status={AvailabilityStatus.AVAILABLE} />
          </div>
        </div>
      ) : (
        <>
          <div className="flex gap-3 mb-4">
            <div className="flex-1">
              <Input
                type="date"
                value={date}
                onChange={(e) => {
                  setDate(e.target.value);
                  setAvailability(null);
                  setError(null);
                }}
                min={new Date().toISOString().split('T')[0]}
                className="w-full"
              />
            </div>
            <Button
              onClick={handleCheck}
              isLoading={loading}
              disabled={!date}
              className="px-6"
            >
              Check
            </Button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {availability && (
            <div className="space-y-4">
              <ProgressBar
                booked={availability.bookedCount}
                max={availability.maxCapacity}
              />

              <div className="grid grid-cols-2 gap-4 pt-3 border-t border-gray-100">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">
                    Trip Name
                  </p>
                  <p className="text-sm font-medium text-gray-900 mt-0.5">
                    {availability.tripName}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">
                    Trip Type
                  </p>
                  <p className="text-sm font-medium text-gray-900 mt-0.5">
                    {availability.tripType}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">
                    Selected Date
                  </p>
                  <p className="text-sm font-medium text-gray-900 mt-0.5">
                    {new Date(availability.date).toLocaleDateString('en-US', {
                      weekday: 'short',
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">
                    Max Capacity
                  </p>
                  <p className="text-sm font-medium text-gray-900 mt-0.5">
                    {availability.maxCapacity} passengers
                  </p>
                </div>
              </div>
            </div>
          )}

          {!availability && !error && !loading && (
            <div className="text-center py-6 text-gray-500">
              <svg
                className="w-10 h-10 mx-auto mb-2 text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <p className="text-sm">Select a date to check availability</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
